import { useState, useEffect } from "react";
import { Navigation, Footer } from "../components/landing/Layout";
import { detectMobileOS, isStandalone, isChromeOnIOS } from "../utils/installState";
import { Bell, CheckCircle2, ChevronDown, Copy, MoreVertical, Smartphone } from "lucide-react";
import { toast } from "sonner";
import InstallOneTapButton from "../components/push/InstallOneTapButton";
import { useI18n } from "../i18n";
import { StepBigIcon, IosShareGlyph } from "../components/install/StepScenes";

const GOLD = "#D4A017";

// iter72 CORTE 3 v2 — una acción por tarjeta: número dorado grande +
// ícono grande reconocible + título en negrita + una línea de ayuda.
// Progreso visible ("Paso 3 de 12"). Mobile-first, sin mockups falsos.

const StepCard = ({ step, index, os, total }) => {
  const { t } = useI18n();
  return (
    <li
      className="ac-step-in bg-white rounded-2xl border border-slate-200 shadow-sm p-5"
      style={{ animationDelay: `${Math.min(index * 60, 500)}ms` }}
      data-testid={`install-step-${os}-${index + 1}`}
    >
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">
        {t("install.progress", { n: index + 1, total })}
      </p>
      <div className="flex items-center gap-4">
        <span
          className="flex-shrink-0 w-14 h-14 rounded-2xl font-extrabold flex items-center justify-center text-black text-3xl shadow"
          style={{ backgroundColor: GOLD }}
        >
          {index + 1}
        </span>
        <StepBigIcon os={os} index={index} />
        <p className="text-slate-900 font-bold text-lg leading-snug flex-1 min-w-0">{step.title}</p>
      </div>
      {step.detail && <p className="text-slate-500 text-sm mt-3">{step.detail}</p>}
    </li>
  );
};

const OSCard = ({ os, testid, topExtra }) => {
  const { t } = useI18n();
  const isIOS = os === "ios";
  const steps = t(isIOS ? "install.ios_steps" : "install.android_steps") || [];
  return (
    <div className="rounded-3xl border overflow-hidden bg-slate-50/60" style={{ borderColor: `${GOLD}55` }} data-testid={testid}>
      <div className="p-5 sm:p-6 border-b bg-white" style={{ borderColor: `${GOLD}33` }}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: GOLD }}>
            {isIOS ? <IosShareGlyph className="w-6 h-6 text-black" /> : <MoreVertical className="w-6 h-6 text-black" />}
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#0B2545]" style={{ fontFamily: "Manrope, sans-serif" }}>
              {t(isIOS ? "install.ios_title" : "install.android_title")}
            </h3>
            <p className="text-slate-500 text-sm">{t(isIOS ? "install.ios_browser" : "install.android_browser")}</p>
          </div>
        </div>
        {topExtra}
      </div>
      <ol className="p-4 sm:p-5 space-y-4" data-testid={`${testid}-steps`}>
        {steps.map((step, i) => <StepCard key={i} step={step} index={i} os={os} total={steps.length} />)}
      </ol>
      <div className="mx-4 sm:mx-5 mb-5 p-4 rounded-xl bg-amber-50 border border-amber-200">
        <p className="text-amber-800 text-sm">
          <strong>{t(isIOS ? "install.ios_warning_label" : "install.android_warning_label")}</strong>{" "}
          {t(isIOS ? "install.ios_warning" : "install.android_warning")}
        </p>
      </div>
    </div>
  );
};

export default function InstallGuide() {
  const [detectedOS] = useState(() => detectMobileOS());
  const [chromeOnIphone] = useState(() => isChromeOnIOS());
  const [standalone] = useState(() => isStandalone());
  const [showOther, setShowOther] = useState(false);
  const { t } = useI18n();

  // iter69-B B3 — flecha animada al botón Compartir de Safari (abajo en iPhone)
  const [showIosArrow, setShowIosArrow] = useState(false);
  useEffect(() => {
    if (detectedOS === "ios" && !chromeOnIphone && !standalone) {
      const tm = setTimeout(() => setShowIosArrow(true), 800);
      return () => clearTimeout(tm);
    }
    return undefined;
  }, [detectedOS, chromeOnIphone, standalone]);

  const copyPageLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t("install.copy_ok"));
    } catch (_) {
      toast.error(t("install.copy_fail"));
    }
  };

  // 3.2 — SOLO la guía del dispositivo detectado primero; la otra colapsada.
  const primaryOS = detectedOS === "android" ? "android" : detectedOS === "ios" ? "ios" : null;
  const otherOS = primaryOS === "ios" ? "android" : "ios";
  const androidExtra = <InstallOneTapButton className="mt-4" />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="install-guide-page">
      <Navigation />

      <section className="bg-[#0B2545] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold mb-6 text-black"
            style={{ backgroundColor: GOLD }}
          >
            <Bell className="w-4 h-4" />
            {t("install.badge")}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: "Manrope, sans-serif" }}>
            {t("install.title")}
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            {t("install.subtitle")}
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 3.2 — "Chrome en iPhone": aviso destacado + copiar link para Safari */}
          {chromeOnIphone && (
            <div className="max-w-2xl mx-auto mb-8 p-4 rounded-2xl bg-red-50 border border-red-300" data-testid="chrome-ios-warning">
              <p className="text-red-800 text-sm font-bold mb-2">{t("install.chrome_ios_title")}</p>
              <p className="text-red-700 text-xs mb-3">{t("install.chrome_ios_text")}</p>
              <button
                type="button"
                onClick={copyPageLink}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-white bg-red-600 hover:bg-red-500 transition-colors"
                data-testid="chrome-ios-copy-link-btn"
              >
                <Copy className="w-4 h-4" /> {t("install.chrome_ios_copy")}
              </button>
            </div>
          )}

          {primaryOS ? (
            <>
              <p className="text-center text-slate-600 mb-8" data-testid="install-guide-detected-os">
                {t("install.detected_prefix")} <strong>{primaryOS === "ios" ? "iPhone" : "Android"}</strong> {t("install.detected_suffix")}
              </p>
              <div className="max-w-xl mx-auto">
                <OSCard
                  os={primaryOS}
                  testid={`install-card-${primaryOS}`}
                  topExtra={primaryOS === "android" ? androidExtra : undefined}
                />
                {/* La otra guía, colapsada */}
                <button
                  type="button"
                  onClick={() => setShowOther((v) => !v)}
                  className="mt-8 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border border-slate-300 bg-white text-slate-700 text-sm font-bold hover:bg-slate-100 transition-colors"
                  data-testid="install-show-other-btn"
                >
                  <Smartphone className="w-4 h-4" />
                  {t(otherOS === "ios" ? "install.show_other_ios" : "install.show_other_android")}
                  <ChevronDown className={`w-4 h-4 transition-transform ${showOther ? "rotate-180" : ""}`} />
                </button>
                {showOther && (
                  <div className="mt-4">
                    <OSCard
                      os={otherOS}
                      testid={`install-card-${otherOS}`}
                      topExtra={otherOS === "android" ? androidExtra : undefined}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="grid md:grid-cols-2 gap-8 items-start">
              <OSCard os="ios" testid="install-card-ios" />
              <OSCard os="android" testid="install-card-android" topExtra={androidExtra} />
            </div>
          )}

          <div className="mt-12 max-w-2xl mx-auto text-center bg-white rounded-2xl border border-slate-200 p-6">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-3" style={{ color: GOLD }} />
            <h2 className="text-lg font-bold text-[#0B2545] mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
              {t("install.scanner_title")}
            </h2>
            <p className="text-slate-600 text-sm">
              {t("install.scanner_text")}
            </p>
          </div>
        </div>
      </section>

      <Footer />

      {/* iter69-B B3 — flecha animada apuntando al botón Compartir de Safari (abajo) */}
      {showIosArrow && (
        <div
          className="fixed bottom-3 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center animate-bounce pointer-events-none"
          data-testid="ios-share-arrow"
        >
          <div className="px-3 py-1.5 rounded-full text-xs font-bold text-black shadow-lg mb-1" style={{ backgroundColor: GOLD }}>
            {t("install.ios_arrow_hint")} <IosShareGlyph className="w-3.5 h-3.5 inline" />
          </div>
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 4v14" />
            <path d="M6 12l6 6 6-6" />
          </svg>
        </div>
      )}
    </div>
  );
}
